import { useMatch } from "react-router-dom";
import { Box, Button, CardContent, Stack, Typography as T } from "@mui/material";
import DirectoryHeader from "files/components/DirectoryHeader";
import FileRow from "files/components/FileRow";
import { getFileTitle } from "files/hooks/useFileTitle";
import { resolveRouterPath, useFilesSearch } from "files/hooks/useFiles";
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";
import { LlamaIcon } from "~/assets/icons";
import LimitedPagination from "~/components/LimitedPagination";
import ListCard from "~/components/ListCard";
import useProjectsAccessList from "~/hooks/useProjectsAccessList";
import useIsWorkbenchFileContext from "~/pages/files/hooks/useIsWorkbenchFileContext";

const PAGE_SIZE = 15;

const SearchResults = () => {
  const [params, setParams] = useQueryParams({
    q: withDefault(StringParam, ""),
    page: withDefault(NumberParam, 1),
    root: BooleanParam,
  });
  const isGlobalFileContext = !useIsWorkbenchFileContext();
  const projectAccess = useProjectsAccessList();
  const { data: searchResponse, isLoading, error } = useFilesSearch();
  const response = searchResponse?.files;
  const truncated = searchResponse?.truncated;
  const showNoResults = !isLoading && response?.length === 0;

  // Calculate the total number of pages
  const totalNumberOfPages = Math.ceil((response?.length ?? 0) / PAGE_SIZE);
  const isLastPage = params.page === totalNumberOfPages;

  // Paginate client side
  const start = params.page ? (params.page - 1) * PAGE_SIZE : 0;
  const paginatedFiles = response?.slice(start, start + PAGE_SIZE) ?? [];

  const clearSearch = () => setParams({ q: undefined, page: undefined, root: undefined });

  if (showNoResults || error?.status === 504) {
    return (
      <CardContent
        sx={{
          border: "1px solid",
          borderColor: "grey.300",
          borderRadius: 2,
          bgcolor: "common.pageBackground",
        }}
      >
        <Stack gap={3} minHeight={300} alignItems="center" justifyContent="center">
          <LlamaIcon
            opacity={0.4}
            width={50}
            color="primary.dark"
            transform={"matrix(-1, 0, 0, 1, 0, 0), scale(1.2)"}
          />
          <T variant="body2">
            {showNoResults ? "No results found" : "Search took too long to complete"}
          </T>
          <Button variant="contained" onClick={clearSearch}>
            Back to Files
          </Button>
        </Stack>
      </CardContent>
    );
  }

  return (
    <>
      <ListCard
        header={<DirectoryHeader />}
        loading={isLoading}
        placeholder={{ height: 34 }}
        sx={{ border: "1px solid", borderColor: "grey.300" }}
      >
        {paginatedFiles.map((file) => {
          const pathSegments = resolveRouterPath(file.key, isGlobalFileContext).split("/");
          const { title: rootTitle } = getFileTitle(
            pathSegments[0],
            isGlobalFileContext,
            projectAccess
          );
          const subtitle = [rootTitle, ...pathSegments.slice(1)].join("/");
          return (
            <FileRow
              key={file.key}
              subtitle={subtitle}
              fileKey={file.key}
              type={file.type}
              size={file.size}
              pendingAction={file.pending_action}
              updatedAt={file.updated_at}
              s3Uri={file.s3_uri}
            />
          );
        })}
      </ListCard>
      <Box mt={1}>
        {!isLoading && (
          <LimitedPagination total={response?.length ?? 0} defaultPageSize={PAGE_SIZE} />
        )}
        {truncated && isLastPage && (
          <T variant="body2" color="textSecondary" align="center">
            Search result limit exceeded. Try refining your search
          </T>
        )}
      </Box>
    </>
  );
};

export default SearchResults;
